import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Checkbox,
  Button,
  Banner,
  Box,
  Spinner,
  Link,
  Badge,
  Tabs,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import {
  fetchClientOpenInvoices,
  applyAllocations,
  foliosInConcept,
  invoiceMatchesConcept,
} from "../../utility/payments";
import { UserAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import PayerPicker from "./PayerPicker";
import CardBadge from "./CardBadge";

// Apply one received payment to what it paid for.
//
// A payment only records that money arrived and from whom. Saying what it
// covers is this step, and it is always a person's decision: the transfer's
// concepto ("renta 2 dias") does not identify a reservation, and guessed
// allocations are the bug class the new payments model exists to remove. The
// one exception is an EXACT match, which arrives pre-checked but still needs
// the click.
//
// Only things still owed are listed — each one a line to tick. Ticking takes the
// full pending amount (capped at what is left of the payment); the amount can
// be edited for a partial payment. Whatever is not applied stays as saldo a
// favor. Everything is in integer cents.
//
// Reused by the Pagos recibidos inbox and, later, from inside a reservation.

export const toCents = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
export const fromCents = (c) => (c / 100).toFixed(2);
const money = (c) => formatPrice(c / 100);

const PURPOSE_LABEL = { rent: "Renta", deposit: "Depósito", invoice: "Factura", refund: "Devolución" };

// The folio matcher lives in utility/payments.js (the statement export uses
// it too); re-exported so existing imports keep working.
export { foliosInConcept, invoiceMatchesConcept } from "../../utility/payments";

// Folios and reservations are separate tabs, not one list. A payment applied to
// a folio and a payment applied to a reservation are different movements — the
// first ties to a CFDI and can oblige a complemento, the second is loose money
// against a rental — and mixing them in a single confirmation makes it easy to
// do one while meaning the other. Switching wipes what was ticked, so nothing
// crosses over unnoticed.
// Only what this client actually has is offered. A tab that opens on "no tiene
// nada" is a dead end the person has to back out of, and for most customers —
// who are never invoiced per folio — the folio tab would be exactly that.
const TAB_LABEL = { invoices: "Facturas por cobrar", reservations: "Reservaciones" };

function rangeLabel(dr) {
  // Postgres range text: ["2026-09-19 15:00:00+00","2026-09-20 15:00:00+00"]
  const parts = String(dr || "").match(/"([^"]+)"/g);
  if (!parts) return "";
  // Postgres writes the offset as "+00"; Date needs "+00:00".
  const fmt = (s) =>
    new Date(s.replace(/"/g, "").replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00")).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
    });
  return parts.length > 1 ? `${fmt(parts[0])} – ${fmt(parts[1])}` : fmt(parts[0]);
}

// What a reservation still owes. Rent: any non-cancelled reservation with a
// balance. Deposit: only while the reservation is active — on a completed one
// the car is back, and an unrecorded deposit there is a data gap, not a debt.
// `payerIds` is every owner the payment may cover: the payer, plus — for a
// company — the razones sociales that use its CLABE (paid_by_company_id).
async function loadPending(payerType, payerIds) {
  const col = payerType === "company" ? "company" : "customer";
  const { data: res } = await supabase
    .from("reservations")
    .select("id, status, date_range, balance, deposit, company, companies(name), car:cars(plate, model:car_models(name))")
    .in(col, payerIds)
    .neq("status", "cancelled")
    .or("balance.gt.0,deposit.gt.0")
    .order("id", { ascending: true })
    .limit(100);

  const ids = (res || []).map((r) => r.id);
  const depositIn = {};
  if (ids.length) {
    const { data: deps } = await supabase
      .from("transactions")
      .select("reservation, amount")
      .in("reservation", ids)
      .eq("type", "income")
      .eq("isDeposit", true);
    for (const d of deps || []) {
      depositIn[d.reservation] = (depositIn[d.reservation] || 0) + Math.round(d.amount * 100);
    }
  }

  return (res || [])
    .map((r) => {
      const lines = [];
      const rent = Math.max(0, Math.round((r.balance || 0) * 100));
      if (rent > 0) lines.push({ purpose: "rent", owed: rent });
      if (r.status === "active") {
        const dep = Math.max(0, Math.round((r.deposit || 0) * 100) - (depositIn[r.id] || 0));
        if (dep > 0) lines.push({ purpose: "deposit", owed: dep });
      }
      return {
        id: r.id,
        company: r.company,
        companyName: r.companies?.name || null,
        label: [r.car?.model?.name, r.car?.plate, rangeLabel(r.date_range)].filter(Boolean).join(" · "),
        lines,
      };
    })
    .filter((r) => r.lines.length > 0);
}

// `reservationId` (optional) is the reservation the modal was opened from. Every
// pending reservation of the client is still listed — one transfer often covers
// several — but that one goes first, is marked, and gets the prefill.
function ApplyPaymentModal({ payment, payerName, unappliedCents: rawUnappliedCents, open, onClose, onApplied, reservationId = null }) {
  const { session, hasPermission } = UserAuth();
  const { branch } = useBranch();
  const navigate = useNavigate();
  // Refund egresos for this payment ("Devolver"). While one still owes the
  // money, that amount is held: the database refuses to apply it elsewhere,
  // so it is taken out of what this modal offers to place.
  const [refunds, setRefunds] = useState([]);
  const heldCents = refunds.reduce((s, r) => s + r.held_cents, 0);
  const unappliedCents = Math.max(0, rawUnappliedCents - heldCents);
  const [refunding, setRefunding] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  // A Clip charge has no account behind it: it goes back by SPEI to a CLABE
  // the bank confirms is the payer's own, typed here.
  const [refundClabe, setRefundClabe] = useState("");
  const clipRefund = payment?.rail === "card" && payment?.source === "clip";
  const canRefund =
    !!hasPermission?.("services.pay") &&
    ((payment?.rail === "spei" &&
      ["customer", "company"].includes(payment?.payer_type) &&
      String(payment?.counterparty_account || "").length === 18) ||
      clipRefund) &&
    unappliedCents > 0;
  const [pending, setPending] = useState(null);
  const [invoices, setInvoices] = useState([]);
  // Folio numbers for invoices this payment already settled. They drop out of
  // the open-invoices list the moment they close, and "a dónde se aplicó"
  // must still name them rather than showing a bare row id.
  const [invoiceNames, setInvoiceNames] = useState({});
  const [applications, setApplications] = useState([]);
  const [picked, setPicked] = useState({}); // `${resId}:${purpose}` -> amount string
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Who a card payment turned out to belong to, chosen here but NOT yet written
  // to the payment. `payments.payer_type` can only ever be set once — the
  // immutability guard allows null -> value and nothing else — so a wrong pick
  // would be permanent. It is committed in apply(), when the person commits.
  const [chosen, setChosen] = useState(null);
  const [tabId, setTabId] = useState("invoices");
  // Shared CLABE: { companyId: name } for the payer and every razón social that
  // pays through its CLABE. Only shown when there is more than one.
  const [razones, setRazones] = useState({});
  const multiRazon = Object.keys(razones).length > 1;
  // Automatic ticking happens once, on the tab the modal opens on. After the
  // person switches tabs themselves, nothing is ticked for them again —
  // otherwise "switching clears the selection" would immediately un-clear.
  const prefilled = useRef(false);
  const tabChosen = useRef(false);

  const payerType = payment?.payer_type || chosen?.payer_type || null;
  const payerId = payment?.payer_type ? payment.payer_id : chosen?.payer_id || null;
  // Searching by reservation or folio already named a reservation; treat it
  // exactly like opening the modal from inside that reservation.
  const focusRes = reservationId ?? chosen?.focusReservationId ?? null;

  // Nothing left to place: the question is no longer "where does this go" but
  // "where did it go", so the tabs and the picker are gone entirely.
  const fullyApplied = unappliedCents <= 0;

  const visibleTabs = useMemo(() => {
    if (fullyApplied) return [];
    const out = [];
    // Folios first when both exist: a client billed per folio pays per folio.
    if (invoices.length) out.push({ id: "invoices", content: TAB_LABEL.invoices });
    if (pending?.length) out.push({ id: "reservations", content: TAB_LABEL.reservations });
    return out;
  }, [invoices.length, pending, fullyApplied]);

  // The chosen tab may vanish (an invoice gets paid elsewhere, say), so fall
  // back to whatever is still there rather than rendering an empty panel.
  const tabIndex = Math.max(0, visibleTabs.findIndex((t) => t.id === tabId));
  const tab = visibleTabs[tabIndex]?.id || null;

  const load = useCallback(async () => {
    if (!payerType) return;
    let ids = [payerId];
    if (payerType === "company") {
      const { data: covered } = await supabase
        .from("companies")
        .select("id, name")
        .or(`id.eq.${Number(payerId)},paid_by_company_id.eq.${Number(payerId)}`);
      setRazones(Object.fromEntries((covered || []).map((c) => [c.id, c.name])));
      ids = [...new Set([Number(payerId), ...(covered || []).map((c) => c.id)])];
    } else {
      setRazones({});
    }
    const [rows, openInvoices, { data: apps }, { data: refundEgresos }] = await Promise.all([
      loadPending(payerType, ids),
      fetchClientOpenInvoices({ clientType: payerType, clientId: payerId, companyIds: ids }),
      supabase
        .from("payment_applications")
        .select("id, reservation_id, invoice_id, purpose, amount_cents, reverses_id, applied_at")
        .eq("payment_id", payment.id)
        .order("id"),
      supabase
        .from("egresos")
        .select("id, closed_at, approval")
        .eq("refund_payment_id", payment.id)
        .is("closed_at", null)
        .neq("approval", "rechazada"),
    ]);
    const refundIds = (refundEgresos || []).map((g) => g.id);
    const { data: refundBals } = refundIds.length
      ? await supabase.from("egreso_balances").select("egreso_id, por_pagar_cents").in("egreso_id", refundIds)
      : { data: [] };
    setRefunds(
      (refundBals || [])
        .map((b) => ({ egreso_id: b.egreso_id, held_cents: Number(b.por_pagar_cents) }))
        .filter((r) => r.held_cents > 0)
    );
    setInvoices(openInvoices || []);

    const appliedInvoiceIds = [...new Set((apps || []).map((a) => a.invoice_id).filter(Boolean))];
    if (appliedInvoiceIds.length) {
      const { data: named } = await supabase
        .from("invoices_issued")
        .select("id, invoice_number")
        .in("id", appliedInvoiceIds);
      setInvoiceNames(Object.fromEntries((named || []).map((i) => [i.id, i.invoice_number])));
    } else {
      setInvoiceNames({});
    }
    const focus = Number(focusRes);
    setPending(focusRes ? [...rows].sort((a, b) => (b.id === focus) - (a.id === focus)) : rows);
    setApplications(apps || []);
  }, [payment, payerType, payerId, focusRes]);

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setError(null);
    setPending(null);
    setInvoices([]);
    setInvoiceNames({});
    setRazones({});
    setChosen(null);
    setTabId("invoices");
    setRefunds([]);
    setRefunding(false);
    setRefundReason("");
    prefilled.current = false;
    tabChosen.current = false;
  }, [open, payment?.id]);

  // Open on whichever tab has something to do. Invoices win when both do:
  // a client who bills by folio pays by folio.
  useEffect(() => {
    if (tabChosen.current || pending === null) return;
    tabChosen.current = true;
    setTabId(invoices.length ? "invoices" : "reservations");
  }, [pending, invoices.length]);

  const switchTab = (i) => {
    setTabId(visibleTabs[i]?.id || "invoices");
    setPicked({});
    prefilled.current = true;
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Pre-check only an unambiguous exact match: one reservation whose pending
  // total equals what is left, or one single line that does.
  // A concepto that names folios is the client telling us what they paid. It
  // beats every amount heuristic below, so it runs first and stops there.
  useEffect(() => {
    if (prefilled.current || tab !== "invoices") return;
    if (!invoices.length || !unappliedCents) return;
    const folios = foliosInConcept(payment.concept);
    if (!folios.length) return;
    let hits = invoices.filter((i) => invoiceMatchesConcept(i.invoice_number, folios));
    if (!hits.length) return;
    // One complemento per razón social: prefill only the first one's folios.
    hits = hits.filter((i) => i.company_id === hits[0].company_id);
    let room = unappliedCents;
    const next = {};
    for (const inv of hits) {
      const due = Math.round(inv.due * 100);
      const take = Math.min(due, room);
      if (take <= 0) break;
      next[`inv:${inv.id}`] = fromCents(take);
      room -= take;
    }
    prefilled.current = true;
    setPicked(next);
  }, [invoices, unappliedCents, payment.concept, tab]);

  useEffect(() => {
    if (prefilled.current || tab !== "reservations") return;
    if (!pending || !unappliedCents) return;
    prefilled.current = true;
    if (focusRes) {
      // The person already chose the reservation. Check every line when the
      // payment covers all of it; otherwise only a line it matches exactly.
      const r = pending.find((x) => x.id === Number(focusRes));
      const next = {};
      if (r) {
        const total = r.lines.reduce((s, l) => s + l.owed, 0);
        const exact = r.lines.filter((l) => l.owed === unappliedCents);
        if (unappliedCents >= total) {
          for (const l of r.lines) next[`${r.id}:${l.purpose}`] = fromCents(l.owed);
        } else if (exact.length === 1) {
          next[`${r.id}:${exact[0].purpose}`] = fromCents(exact[0].owed);
        }
      }
      setPicked(next);
      return;
    }
    const whole = pending.filter((r) => r.lines.reduce((s, l) => s + l.owed, 0) === unappliedCents);
    const single = pending.flatMap((r) => r.lines.filter((l) => l.owed === unappliedCents).map((l) => ({ r, l })));
    const next = {};
    if (whole.length === 1 && single.length <= (whole[0].lines.length === 1 ? 1 : 0)) {
      for (const l of whole[0].lines) next[`${whole[0].id}:${l.purpose}`] = fromCents(l.owed);
    } else if (whole.length === 0 && single.length === 1) {
      next[`${single[0].r.id}:${single[0].l.purpose}`] = fromCents(single[0].l.owed);
    }
    setPicked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, unappliedCents, focusRes, tab]);

  // Two kinds of target share one map: "inv:1271" is a folio, "218:rent" is a
  // reservation line.
  const items = useMemo(
    () =>
      Object.entries(picked).map(([k, v]) => {
        if (k.startsWith("inv:")) {
          return { invoice_id: Number(k.slice(4)), purpose: "invoice", amount_cents: toCents(v) };
        }
        const [reservation_id, purpose] = k.split(":");
        return { reservation_id: Number(reservation_id), purpose, amount_cents: toCents(v) };
      }),
    [picked]
  );
  // The razón social of the folios already ticked (undefined when none).
  const pickedInvoiceCompany = useMemo(() => {
    const first = items.find((i) => i.purpose === "invoice");
    return first ? invoices.find((inv) => inv.id === first.invoice_id)?.company_id ?? null : undefined;
  }, [items, invoices]);
  // Picking a person for a company's reservation (the driver) stamps the
  // COMPANY as payer — apply_payment does the same; this only shows it.
  // Mixing company and personal targets is refused, since a payment has one payer.
  const stampAs = useMemo(() => {
    if (payment?.payer_type || chosen?.payer_type !== "customer") return null;
    const rows = items
      .filter((i) => i.reservation_id)
      .map((i) => (pending || []).find((r) => r.id === i.reservation_id))
      .filter(Boolean);
    const companies = [...new Set(rows.map((r) => r.company).filter(Boolean))];
    if (!companies.length) return null;
    if (companies.length > 1 || rows.some((r) => !r.company) || items.some((i) => i.purpose === "invoice")) {
      return { mixed: true };
    }
    return { name: rows.find((r) => r.company === companies[0]).companyName || "la empresa" };
  }, [payment, chosen, items, pending]);
  const invalid = items.some((i) => !Number.isInteger(i.amount_cents) || i.amount_cents <= 0);
  const assigned = items.reduce((s, i) => s + (Number.isInteger(i.amount_cents) ? i.amount_cents : 0), 0);
  const remaining = unappliedCents - assigned;

  const toggle = (resId, line, on) =>
    setPicked((p) => {
      const key = `${resId}:${line.purpose}`;
      const next = { ...p };
      if (!on) {
        delete next[key];
        return next;
      }
      const used = Object.entries(p).reduce((s, [k, v]) => s + (k === key ? 0 : toCents(v) || 0), 0);
      const room = Math.max(0, unappliedCents - used);
      next[key] = fromCents(Math.min(line.owed, room));
      return next;
    });

  const toggleInvoice = (inv, on) =>
    setPicked((p) => {
      const key = `inv:${inv.id}`;
      const next = { ...p };
      if (!on) {
        delete next[key];
        return next;
      }
      const used = Object.entries(p).reduce((sum, [k, v]) => sum + (k === key ? 0 : toCents(v) || 0), 0);
      const room = Math.max(0, unappliedCents - used);
      next[key] = fromCents(Math.min(Math.round(inv.due * 100), room));
      return next;
    });

  const apply = async () => {
    setBusy(true);
    setError(null);
    // A payment identified here is stamped by apply_payment itself, in the
    // same transaction, so a refused application never leaves a permanent
    // wrong payer behind. A person picked for a company's reservation is
    // stamped as the company (see stampAs).
    const { data: written, error: err } = await supabase.rpc("apply_payment", {
      p_payment_id: payment.id,
      p_items: items,
      p_note: null,
      ...(!payment.payer_type && chosen
        ? { p_payer_type: chosen.payer_type, p_payer_id: chosen.payer_id }
        : {}),
    });
    if (err) {
      setBusy(false);
      return setError(err.message);
    }

    // Invoice applications are only half done here. apply_payment wrote the
    // ledger and the legacy transaction, with the money still UNALLOCATED;
    // applyAllocations now ties it to each folio, zeroes that unallocated
    // amount and — for a PPD folio — stamps the complemento de pago in Alegra.
    // All the folios of one payment share one transaction, so this is a single
    // call and therefore a single complemento listing each folio.
    let adoptedStamp = false;
    const invoiceApps = (written || []).filter((a) => a.invoice_id && a.amount_cents > 0);
    if (invoiceApps.length) {
      const byId = new Map(invoices.map((i) => [i.id, i]));
      const transactionId = invoiceApps[0].legacy_transaction_id;
      const allocations = invoiceApps
        .map((a) => ({ invoice: byId.get(a.invoice_id), amount: a.amount_cents / 100 }))
        .filter((a) => a.invoice);
      try {
        const res = await applyAllocations({
          transactionId,
          transactionAmount: invoiceApps.reduce((sum, a) => sum + a.amount_cents, 0) / 100,
          allocations,
          createdBy: session?.user?.id,
        });
        adoptedStamp = !!res?.adoptedStamp;
      } catch (e) {
        setBusy(false);
        // The ledger row stands and the money is on the client's account; only
        // the invoice link and the complemento are missing. Say exactly that,
        // because "failed" would invite someone to apply it a second time.
        await load();
        return setError(
          "El pago quedó registrado, pero no se pudo asignar a la factura ni timbrar el complemento: " +
            (e?.message || e) +
            ". Asígnalo desde la factura."
        );
      }
    }

    setBusy(false);
    // Say what happened. Closing in silence after Alegra has printed errors to
    // the console reads as failure, and the natural next move — doing it again —
    // is the one that must not happen with a stamped CFDI.
    const n = items.length;
    const what = items[0]?.purpose === "invoice"
      ? `${n} ${n === 1 ? "factura" : "facturas"}`
      : `${n} ${n === 1 ? "concepto" : "conceptos"}`;
    onApplied?.({
      message: `Se aplicaron ${money(assigned)} a ${what}.` +
        (adoptedStamp
          ? " Alegra reportó un error, pero el complemento sí se timbró y quedó vinculado — no lo vuelvas a aplicar."
          : ""),
      tone: adoptedStamp ? "warning" : "success",
    });
  };

  const startRefund = async () => {
    setRefundAmount(fromCents(unappliedCents));
    setRefundReason("");
    setRefundClabe("");
    setError(null);
    setRefunding(true);
    if (clipRefund && payerType) {
      const { data } = await supabase
        .from(payerType === "company" ? "companies" : "customers")
        .select("bank_clabe")
        .eq("id", payerId)
        .maybeSingle();
      setRefundClabe(String(data?.bank_clabe || "").replace(/\D/g, ""));
    }
  };

  // Creates the egreso and its pending SPEI; paying it (and Face ID on the
  // phone) happens on the egreso page, and the payment closes itself when the
  // SPEI succeeds.
  const createRefund = async () => {
    const cents = toCents(refundAmount);
    setBusy(true);
    setError(null);
    const { data: egresoId, error: err } = await supabase.rpc("create_payment_refund", {
      p_payment_id: payment.id,
      p_amount_cents: cents,
      p_reason: refundReason.trim(),
      p_branch_id: branch?.id || null,
      ...(clipRefund ? { p_clabe: refundClabe } : {}),
      // Identified here: stamped by the refund itself, like apply_payment.
      ...(!payment.payer_type && chosen
        ? { p_payer_type: chosen.payer_type, p_payer_id: chosen.payer_id }
        : {}),
    });
    setBusy(false);
    if (err) return setError(err.message);
    onClose();
    navigate(`/egresos/${egresoId}`);
  };
  const refundCents = toCents(refundAmount);
  const refundInvalid =
    !Number.isFinite(refundCents) || refundCents <= 0 || refundCents > unappliedCents || !refundReason.trim() ||
    (clipRefund && (!payerType || refundClabe.replace(/\D/g, "").length !== 18));

  const reverse = async (appId) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("reverse_payment_application", { p_application_id: appId });
    setBusy(false);
    if (err) return setError(err.message);
    await load();
    onApplied?.({ keepOpen: true });
  };

  const reversed = new Set(applications.filter((a) => a.reverses_id).map((a) => a.reverses_id));
  const live = applications.filter((a) => a.amount_cents > 0 && !reversed.has(a.id));

  // Mirror of the rules in reverse_payment_application(): an application tied
  // to a folio is final (its balance, and possibly a stamped complemento,
  // depend on it), and after two hours a correction needs a deliberate route
  // rather than a button. The database refuses either way — this only avoids
  // offering something that will fail. The deposit-already-returned case is
  // left to the server, which can name the date it went back.
  const REVERT_WINDOW_MS = 2 * 60 * 60 * 1000;
  const canReverse = (a) =>
    !a.invoice_id && Date.now() - new Date(a.applied_at).getTime() <= REVERT_WINDOW_MS;

  // Who the money came from, in the terms of its own rail: a SPEI has a bank
  // and a CLABE, a card has a network mark and four digits.
  const isCard = payment.rail === "card" && (payment.card_brand || payment.card_last4);
  const sender = isCard
    ? null
    : [
        payment.counterparty_name,
        payment.counterparty_bank,
        payment.counterparty_account && `···${payment.counterparty_account.slice(-4)}`,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={refunding ? "Devolver pago" : fullyApplied ? "Pago aplicado" : "Aplicar pago"}
      primaryAction={
        refunding
          ? {
              content: refundCents > 0 && !refundInvalid ? `Devolver ${money(refundCents)}` : "Devolver",
              onAction: createRefund,
              loading: busy,
              disabled: refundInvalid,
            }
          : fullyApplied
          ? undefined
          : {
              content: assigned > 0 ? `Aplicar ${money(assigned)}` : "Aplicar",
              onAction: apply,
              loading: busy,
              disabled: !payerType || !items.length || invalid || remaining < 0 || !!stampAs?.mixed,
            }
      }
      secondaryActions={
        refunding
          ? [{ content: "Volver", onAction: () => { setRefunding(false); setError(null); } }]
          : [
              ...(canRefund ? [{ content: "Devolver", destructive: true, onAction: startRefund }] : []),
              { content: fullyApplied ? "Cerrar" : "Cancelar", onAction: onClose },
            ]
      }
    >
      <Modal.Section>
        <InlineStack align="space-between" blockAlign="start" gap="400" wrap={false}>
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text variant="headingMd" as="h3">
                {payerName || stampAs?.name || chosen?.name || "Sin identificar"}
              </Text>
              {/* Not yet written to the payment — say so, and allow a change. */}
              {!payment.payer_type && chosen && (
                <>
                  <Badge tone="attention">Por confirmar</Badge>
                  <Button variant="plain" onClick={() => setChosen(null)}>Cambiar</Button>
                </>
              )}
            </InlineStack>
            {stampAs?.name && (
              <Text tone="subdued" variant="bodySm">Empresa de la reservación · conductor {chosen.name}</Text>
            )}
            {stampAs?.mixed && (
              <Text tone="critical" variant="bodySm">
                Aplica por separado lo de cada empresa y lo personal: el pago queda a nombre de una sola.
              </Text>
            )}
            {isCard ? (
              <InlineStack gap="150" blockAlign="center" wrap={false}>
                <Text tone="subdued" variant="bodySm">
                  Pagó{payment.counterparty_name ? ` ${payment.counterparty_name}` : ""} con
                </Text>
                <CardBadge brand={payment.card_brand} last4={payment.card_last4} funding={payment.card_type} />
              </InlineStack>
            ) : (
              sender && <Text tone="subdued" variant="bodySm">Envió {sender}</Text>
            )}
            {payment.concept && <Text tone="subdued" variant="bodySm">“{payment.concept}”</Text>}
          </BlockStack>
          <BlockStack gap="100" inlineAlign="end">
            {/* The big number is what still needs placing. Once nothing does,
                "$0.00 de $50,120.00" says nothing useful — the payment is just
                what it always was, so show that. */}
            <Text variant="headingLg" as="p" numeric>
              {money(fullyApplied ? payment.amount_cents : unappliedCents)}
            </Text>
            <Text tone="subdued" variant="bodySm">
              {!fullyApplied && unappliedCents !== payment.amount_cents
                ? `de ${money(payment.amount_cents)} · `
                : ""}
              {new Date(payment.occurred_at).toLocaleString("es-MX", {
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              })}
            </Text>
            {refunds.map((r) => (
              <Link key={r.egreso_id} url={`/egresos/${r.egreso_id}`} removeUnderline>
                <Text as="span" variant="bodySm" tone="caution">
                  {money(r.held_cents)} en devolución · Egreso #{r.egreso_id}
                </Text>
              </Link>
            ))}
          </BlockStack>
        </InlineStack>
      </Modal.Section>

      {refunding ? (
        <Modal.Section>
          <BlockStack gap="300">
            {error && <Banner tone="critical">{error}</Banner>}
            {clipRefund ? (
              <>
                {!payerType && <Banner tone="warning">Primero identifica quién pagó.</Banner>}
                <Text>
                  Se devuelve por SPEI a una cuenta de {payerName || chosen?.name || "quien pagó"}: el banco confirma
                  que es suya antes de enviarse. Al enviarse, este pago queda cerrado por ese monto.
                </Text>
                <TextField
                  label="CLABE"
                  inputMode="numeric"
                  value={refundClabe}
                  onChange={(v) => setRefundClabe(v.replace(/\D/g, "").slice(0, 18))}
                  helpText="Se guarda en su perfil"
                  autoComplete="off"
                  disabled={!payerType}
                />
              </>
            ) : (
              <Text>
                Se crea un egreso con el SPEI listo para enviarse a la cuenta de donde vino
                ({payment.counterparty_bank ? `${payment.counterparty_bank} ` : ""}···{payment.counterparty_account.slice(-4)}).
                Al enviarse, este pago queda cerrado por ese monto.
              </Text>
            )}
            <TextField
              label="Monto a devolver"
              prefix="$"
              inputMode="decimal"
              value={refundAmount}
              onChange={setRefundAmount}
              helpText={`Máximo ${money(unappliedCents)}`}
              error={Number.isFinite(refundCents) && refundCents > unappliedCents ? "Excede lo que queda del pago" : undefined}
              autoComplete="off"
            />
            <TextField
              label="Motivo"
              value={refundReason}
              onChange={setRefundReason}
              placeholder="Depósito enviado por error, pago duplicado…"
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      ) : (
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}

          {/* One option is not a choice — label it instead of drawing a lone tab. */}
          {payerType && visibleTabs.length > 1 && (
            <Tabs tabs={visibleTabs} selected={tabIndex} onSelect={switchTab} fitted />
          )}
          {payerType && visibleTabs.length === 1 && (
            <Text variant="headingSm" as="h4">{visibleTabs[0].content}</Text>
          )}

          {payerType && tab === "invoices" && (
            <BlockStack gap="200">
              {invoices.map((inv) => {
                const key = `inv:${inv.id}`;
                const on = key in picked;
                const due = Math.round(inv.due * 100);
                // A complemento de pago has one receptor, so one application
                // covers one razón social. The rest of the money stays unapplied
                // for the next round.
                const otherRazon = pickedInvoiceCompany !== undefined && inv.company_id !== pickedInvoiceCompany;
                return (
                  <Box key={inv.id} borderWidth="025" borderColor="border" borderRadius="200" padding="300">
                    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="300">
                      <Checkbox
                        label={
                          <InlineStack gap="150" blockAlign="baseline">
                            <Text as="span" fontWeight="semibold">{inv.invoice_number}</Text>
                            {inv.payment_type === "PPD" && <Badge>PPD</Badge>}
                            {inv.reservation_id && (
                              <Text as="span" tone="subdued" variant="bodySm">#{inv.reservation_id}</Text>
                            )}
                            {multiRazon && (
                              <Text as="span" tone="subdued" variant="bodySm">{razones[inv.company_id]}</Text>
                            )}
                          </InlineStack>
                        }
                        helpText={
                          otherRazon
                            ? "Otra razón social: aplícala por separado"
                            : `Pendiente ${money(due)}${inv.date ? ` · ${inv.date}` : ""}`
                        }
                        disabled={otherRazon}
                        checked={on}
                        onChange={(v) => toggleInvoice(inv, v)}
                      />
                      {on && (
                        <div style={{ width: 140 }}>
                          <TextField
                            label={inv.invoice_number}
                            labelHidden
                            prefix="$"
                            inputMode="decimal"
                            align="right"
                            value={picked[key]}
                            onChange={(v) => setPicked((p) => ({ ...p, [key]: v }))}
                            autoComplete="off"
                          />
                        </div>
                      )}
                    </InlineStack>
                  </Box>
                );
              })}
            </BlockStack>
          )}

          {!payerType ? (
            <PayerPicker onPick={setChosen} />
          ) : fullyApplied ? null : pending === null ? (
            <InlineStack align="center"><Spinner size="small" /></InlineStack>
          ) : visibleTabs.length === 0 ? (
            <Text tone="subdued">
              No tiene facturas ni reservaciones con saldo. El dinero queda como saldo a favor.
            </Text>
          ) : tab !== "reservations" ? null : (
            pending.map((r) => (
              <Box key={r.id} borderWidth="025" borderColor="border" borderRadius="200" padding="300">
                <BlockStack gap="200">
                  <InlineStack gap="200" blockAlign="baseline">
                    <Link url={`/reservation?id=${r.id}`} target="_blank" removeUnderline>
                      <Text as="span" fontWeight="semibold">#{r.id}</Text>
                    </Link>
                    <Text as="span" tone="subdued" variant="bodySm">
                      {multiRazon ? [razones[r.company], r.label].filter(Boolean).join(" · ") : r.label}
                    </Text>
                    {focusRes && r.id === Number(focusRes) && (
                      <Badge tone="info">Esta reservación</Badge>
                    )}
                  </InlineStack>
                  {r.lines.map((l) => {
                    const key = `${r.id}:${l.purpose}`;
                    const on = key in picked;
                    return (
                      <InlineStack key={key} align="space-between" blockAlign="center" wrap={false} gap="300">
                        <Checkbox
                          label={PURPOSE_LABEL[l.purpose]}
                          helpText={`Pendiente ${money(l.owed)}`}
                          checked={on}
                          onChange={(v) => toggle(r.id, l, v)}
                        />
                        {on && (
                          <div style={{ width: 140 }}>
                            <TextField
                              label={PURPOSE_LABEL[l.purpose]}
                              labelHidden
                              prefix="$"
                              inputMode="decimal"
                              align="right"
                              value={picked[key]}
                              onChange={(v) => setPicked((p) => ({ ...p, [key]: v }))}
                              autoComplete="off"
                            />
                          </div>
                        )}
                      </InlineStack>
                    );
                  })}
                </BlockStack>
              </Box>
            ))
          )}

          {!fullyApplied && visibleTabs.length > 0 && (
            <InlineStack align="end">
              {remaining < 0 ? (
                <Text tone="critical">Excede el pago por {money(-remaining)}</Text>
              ) : invalid ? (
                <Text tone="critical">Hay un monto inválido</Text>
              ) : remaining > 0 && items.length > 0 ? (
                <Text tone="subdued">{money(remaining)} queda como saldo a favor</Text>
              ) : null}
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
      )}

      {live.length > 0 && (
        <Modal.Section>
          <BlockStack gap="150">
            <Text tone="subdued" variant="bodySm">
              {fullyApplied ? "A dónde se aplicó" : "Ya aplicado"}
            </Text>
            {live.map((a) => (
              <InlineStack key={a.id} align="space-between" blockAlign="center">
                <Text as="span" variant="bodySm">
                  {a.purpose === "invoice"
                    ? `${invoiceNames[a.invoice_id] || invoices.find((i) => i.id === a.invoice_id)?.invoice_number || `Factura ${a.invoice_id}`}${a.reservation_id ? ` · #${a.reservation_id}` : ""} · ${money(a.amount_cents)}`
                    : a.purpose === "refund"
                    ? `${PURPOSE_LABEL.refund} al remitente · ${money(a.amount_cents)}`
                    : `#${a.reservation_id} · ${PURPOSE_LABEL[a.purpose]} · ${money(a.amount_cents)}`}
                </Text>
                {canReverse(a) && (
                  <Button variant="plain" tone="critical" onClick={() => reverse(a.id)} disabled={busy}>
                    Revertir
                  </Button>
                )}
              </InlineStack>
            ))}
          </BlockStack>
        </Modal.Section>
      )}
    </Modal>
  );
}

export default ApplyPaymentModal;
